import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';

const PendingSellers = () => {
  const { user, isAuthenticated } = useUser();
  const navigate = useNavigate();
  const [pendingSellers, setPendingSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const API_URL = 'http://localhost:5000/api';

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchPendingSellers();
  }, [isAuthenticated, user, navigate]);

  const fetchPendingSellers = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/sellers/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setPendingSellers(response.data.sellers);
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to fetch pending sellers'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (sellerId) => {
    if (!window.confirm('Are you sure you want to approve this seller?')) return;

    setProcessing(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/admin/sellers/${sellerId}/approve`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Seller approved successfully!'
        });
        // Remove from list
        setPendingSellers(prev => prev.filter(s => s._id !== sellerId));
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to approve seller'
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setMessage({ type: 'error', text: 'Rejection reason is required' });
      return;
    }

    setProcessing(true);
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/admin/sellers/${selectedSeller._id}/reject`,
        { reason: rejectReason },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setMessage({
          type: 'success',
          text: 'Seller application rejected'
        });
        setShowRejectModal(false);
        setSelectedSeller(null);
        setRejectReason('');
        // Remove from list
        setPendingSellers(prev => prev.filter(s => s._id !== selectedSeller._id));
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.response?.data?.message || 'Failed to reject seller'
      });
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-80">
        <div className="spinner-border text-main-600" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <section className="admin-pending-sellers py-80">
      <div className="container container-lg">
        <div className="d-flex justify-content-between align-items-center mb-40">
          <h1>Pending Seller Applications</h1>
          <button
            onClick={fetchPendingSellers}
            className="btn btn-outline-main"
            disabled={processing}
          >
            <i className="ph ph-arrows-clockwise me-2"></i>
            Refresh
          </button>
        </div>

        {message.text && (
          <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'} mb-24`}>
            {message.text}
          </div>
        )}

        {pendingSellers.length === 0 ? (
          <div className="text-center py-60 bg-light rounded-16">
            <i className="ph ph-check-circle fs-1 text-success mb-16"></i>
            <h5>No pending applications</h5>
            <p className="text-gray-500">All seller applications have been processed</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Applied On</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Store Name</th>
                  <th>Store Address</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingSellers.map(seller => (
                  <tr key={seller._id}>
                    <td>{new Date(seller.sellerAppliedAt).toLocaleDateString()}</td>
                    <td>{seller.username}</td>
                    <td>{seller.email}</td>
                    <td>{seller.phone}</td>
                    <td>{seller.storeName}</td>
                    <td>{seller.storeAddress}</td>
                    <td>
                      <div className="d-flex gap-8">
                        <button
                          onClick={() => handleApprove(seller._id)}
                          className="btn btn-success btn-sm"
                          disabled={processing}
                        >
                          <i className="ph ph-check me-1"></i>
                          Approve
                        </button>
                        <button
                          onClick={() => {
                            setSelectedSeller(seller);
                            setShowRejectModal(true);
                          }}
                          className="btn btn-danger btn-sm"
                          disabled={processing}
                        >
                          <i className="ph ph-x me-1"></i>
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Reject Seller Application</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowRejectModal(false);
                      setSelectedSeller(null);
                      setRejectReason('');
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-16">
                    Rejecting application from <strong>{selectedSeller?.username}</strong>
                  </p>
                  <div className="mb-16">
                    <label className="form-label fw-medium">
                      Rejection Reason <span className="text-danger">*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      placeholder="Explain why the application is being rejected..."
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => {
                      setShowRejectModal(false);
                      setSelectedSeller(null);
                      setRejectReason('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleReject}
                    disabled={processing || !rejectReason.trim()}
                  >
                    {processing ? 'Rejecting...' : 'Reject Application'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PendingSellers;